/**
 * ChannelsPage - Meus Canais
 *
 * Suporta 2 modos por plataforma:
 *   - REAL: redireciona para o OAuth da rede social (quando o admin tiver
 *           configurado as env vars META_CLIENT_ID etc no Railway)
 *   - DEMO: conexão simulada (modo provisório enquanto se aguarda aprovação
 *           da Dev App da plataforma)
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { CHANNELS } from "@/lib/channels";
import { api, getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
    CheckCircle2,
    Loader2,
    Lock,
    Unplug,
    Users,
    ExternalLink,
    AlertTriangle,
    Sparkles,
} from "lucide-react";

const PLATFORM_TYPES = ["facebook", "instagram", "linkedin", "tiktok", "youtube", "x"];

export default function ChannelsPage() {
    const [platforms, setPlatforms] = useState([]); // [{platform, configured, ...}]
    const [accounts, setAccounts] = useState([]); // [{id, platform, mode, status, ...}]
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(null); // platform type when dialog open
    const [handle, setHandle] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [connecting, setConnecting] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    const load = async () => {
        try {
            const [pRes, aRes] = await Promise.all([
                api.get("/social/platforms"),
                api.get("/social/accounts"),
            ]);
            setPlatforms(pRes.data);
            setAccounts(aRes.data);
        } catch (err) {
            toast.error(getApiErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    // Trata querystring após callback OAuth: ?social_connected=facebook ou ?social_error=...
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const connected = params.get("social_connected");
        const error = params.get("social_error");
        if (connected) {
            toast.success(`${capitalize(connected)} ligado com sucesso!`);
            load();
            navigate("/canais", { replace: true });
        } else if (error) {
            toast.error(`Erro a ligar: ${error}`);
            navigate("/canais", { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const platformByType = useMemo(() => {
        const m = {};
        platforms.forEach((p) => { m[p.platform] = p; });
        return m;
    }, [platforms]);

    const accountByType = useMemo(() => {
        const m = {};
        accounts.forEach((a) => { m[a.platform] = a; });
        return m;
    }, [accounts]);

    const startRealOauth = async (platform) => {
        try {
            const { data } = await api.post(`/social/connect/${platform}`, {
                redirect_after: "/canais",
            });
            // Redireciona para a página de autorização da rede social
            window.location.href = data.auth_url;
        } catch (err) {
            // 400 quando platform não está configurada → fallback para demo
            const msg = getApiErrorMessage(err, "");
            if (msg.includes("não tem App configurada")) {
                toast.info("Esta plataforma ainda está em modo Demo (a App Developer ainda não foi configurada no servidor).");
                setOpen(platform);
            } else {
                toast.error(msg);
            }
        }
    };

    const submitDemoConnect = async () => {
        if (!handle.trim()) {
            toast.error("Indique o nome de utilizador / página.");
            return;
        }
        setConnecting(true);
        try {
            await api.post(`/social/connect-demo/${open}`, {
                handle: handle.trim(),
                display_name: displayName.trim() || null,
            });
            toast.success(`${capitalize(open)} ligado em modo Demo.`);
            setOpen(null);
            setHandle("");
            setDisplayName("");
            await load();
        } catch (err) {
            toast.error(getApiErrorMessage(err));
        } finally {
            setConnecting(false);
        }
    };

    const handleDisconnect = async (accountId, platform) => {
        try {
            await api.delete(`/social/accounts/${accountId}`);
            toast.success(`${capitalize(platform)} desligado.`);
            await load();
        } catch (err) {
            toast.error(getApiErrorMessage(err));
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20 text-muted-foreground">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> A carregar canais…
            </div>
        );
    }

    const totalReady = platforms.filter((p) => p.configured).length;

    return (
        <div className="space-y-6" data-testid="channels-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                        Meus Canais
                    </h1>
                    <p className="text-muted-foreground">
                        Ligue todas as redes sociais do seu negócio para publicar com 1 clique.
                    </p>
                </div>
                <Badge className="bg-[#6B21D9]/15 text-[#A78BFA] border-[#6B21D9]/30 border self-start">
                    {totalReady} de {PLATFORM_TYPES.length} plataformas com OAuth real
                </Badge>
            </div>

            {totalReady === 0 && (
                <Card className="p-4 rounded-2xl bg-[#F59E0B]/8 border-[#F59E0B]/30" data-testid="channels-demo-banner">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-[#F59E0B] shrink-0 mt-0.5" />
                        <div className="text-sm">
                            <p className="font-semibold text-foreground">Todas as plataformas estão em Modo Demo</p>
                            <p className="text-muted-foreground mt-1">
                                As publicações são guardadas mas <strong>não são enviadas</strong> às redes reais.
                                Para ativar publicação real, o admin precisa de criar a "Developer App" em cada plataforma e configurar as credenciais no servidor (Railway → Variables).
                                Veja os guias <strong>DEPLOY_OAUTH_*.md</strong> no repositório GitHub.
                            </p>
                        </div>
                    </div>
                </Card>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {CHANNELS.map((c) => {
                    const Icon = c.icon;
                    const platform = platformByType[c.type];
                    const account = accountByType[c.type];
                    const isConnected = !!account;
                    const mode = account?.mode || "real";
                    const platformConfigured = platform?.configured;

                    return (
                        <Card
                            key={c.type}
                            className={`glass-card p-5 rounded-2xl transition-colors ${
                                isConnected
                                    ? mode === "real"
                                        ? "border-[#2DD4BF]/40"
                                        : "border-[#F59E0B]/40"
                                    : "border-white/10 hover:border-[#6B21D9]/40"
                            }`}
                            data-testid={`channel-card-${c.type}`}
                        >
                            <div className="flex items-start justify-between">
                                <span className={`h-12 w-12 rounded-xl ${c.bg} text-white grid place-items-center`}>
                                    <Icon className="h-6 w-6" />
                                </span>
                                {isConnected ? (
                                    mode === "real" ? (
                                        <Badge className="bg-[#2DD4BF]/15 text-[#2DD4BF] border-[#2DD4BF]/25 border gap-1">
                                            <CheckCircle2 className="h-3 w-3" /> Ligado
                                        </Badge>
                                    ) : (
                                        <Badge className="bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/30 border gap-1">
                                            <Sparkles className="h-3 w-3" /> Demo
                                        </Badge>
                                    )
                                ) : platformConfigured ? (
                                    <Badge className="bg-[#6B21D9]/10 text-[#A78BFA] border-[#6B21D9]/20 border">
                                        OAuth pronto
                                    </Badge>
                                ) : (
                                    <Badge className="bg-white/5 text-muted-foreground border-white/10 border">
                                        Modo Demo
                                    </Badge>
                                )}
                            </div>

                            <h3 className="mt-4 font-display font-semibold text-lg">{c.name}</h3>

                            {isConnected ? (
                                <>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        @{account.platform_username}
                                        {account.platform_display_name && account.platform_display_name !== account.platform_username && (
                                            <span className="text-xs"> · {account.platform_display_name}</span>
                                        )}
                                    </p>
                                    {/* Capabilities */}
                                    <div className="flex flex-wrap gap-1 mt-3">
                                        {account.can_publish && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#2DD4BF]/15 text-[#2DD4BF]">
                                                Publicar
                                            </span>
                                        )}
                                        {account.can_publish_video && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#2DD4BF]/15 text-[#2DD4BF]">
                                                Vídeo
                                            </span>
                                        )}
                                        {account.can_publish_stories && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#2DD4BF]/15 text-[#2DD4BF]">
                                                Stories
                                            </span>
                                        )}
                                        {account.can_read_insights && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#A78BFA]/15 text-[#A78BFA]">
                                                Analytics
                                            </span>
                                        )}
                                        {account.can_run_ads && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FB7185]/15 text-[#FB7185]">
                                                Ads
                                            </span>
                                        )}
                                        {mode === "demo" && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B]">
                                                Não publica realmente
                                            </span>
                                        )}
                                    </div>
                                    <Button
                                        variant="outline"
                                        className="w-full mt-4 gap-2"
                                        onClick={() => handleDisconnect(account.id, c.type)}
                                        data-testid={`channels-${c.type}-disconnect-button`}
                                    >
                                        <Unplug className="h-4 w-4" /> Desligar
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {platformConfigured
                                            ? "Autoriza a app a publicar em seu nome."
                                            : "Configuração em curso — pode usar modo demo entretanto."}
                                    </p>
                                    {platformConfigured ? (
                                        <Button
                                            className="w-full mt-4 btn-grad-purple text-white gap-2"
                                            onClick={() => startRealOauth(c.type)}
                                            data-testid={`channels-${c.type}-connect-real-button`}
                                        >
                                            <ExternalLink className="h-4 w-4" /> Ligar via {c.name}
                                        </Button>
                                    ) : (
                                        <Button
                                            className="w-full mt-4 gap-2"
                                            variant="outline"
                                            onClick={() => {
                                                setOpen(c.type);
                                                setHandle("");
                                                setDisplayName("");
                                            }}
                                            data-testid={`channels-${c.type}-connect-demo-button`}
                                        >
                                            <Lock className="h-4 w-4" /> Ligar (Demo)
                                        </Button>
                                    )}
                                </>
                            )}
                        </Card>
                    );
                })}
            </div>

            <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
                <DialogContent className="sm:max-w-md" data-testid="channel-connect-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            Ligar {open ? capitalize(open) : ""} (Demo)
                        </DialogTitle>
                        <DialogDescription>
                            <span className="flex items-center gap-1.5 text-[#F59E0B] mb-2 mt-1">
                                <Users className="h-3.5 w-3.5" /> Modo de demonstração
                            </span>
                            Esta plataforma ainda está em <strong>modo Demo</strong>. As publicações
                            são guardadas mas <strong>NÃO são publicadas</strong> na rede real até a
                            Developer App ser configurada pelo admin.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label htmlFor="handle">Nome de utilizador / página</Label>
                            <Input
                                id="handle"
                                placeholder="@minhaempresa"
                                value={handle}
                                onChange={(e) => setHandle(e.target.value)}
                                data-testid="channel-connect-handle-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="display-name">Nome a mostrar (opcional)</Label>
                            <Input
                                id="display-name"
                                placeholder="A Minha Empresa Lda"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                data-testid="channel-connect-display-name-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(null)}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={submitDemoConnect}
                            disabled={connecting}
                            className="btn-grad-purple text-white"
                            data-testid="channel-connect-confirm-button"
                        >
                            {connecting ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> A ligar…
                                </>
                            ) : (
                                "Ligar (Demo)"
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function capitalize(s) {
    if (!s) return "";
    if (s === "x") return "X / Twitter";
    return s.charAt(0).toUpperCase() + s.slice(1);
}
